import { useDraggable, useDroppable } from "@dnd-kit/core";
import { checkPermissions } from "@elit-hair/elit-tools";
import InboxOutlined from "@mui/icons-material/InboxOutlined";
import { GridColDef, GridEventListener, GridRow, GridRowProps } from "@mui/x-data-grid";
import { GridCellSelectionModel } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { motion } from "motion/react";
import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";

import CommonGridTable from "@/components/CommonGridTable";
import { CompanionsColumn } from "@/components/table/CompanionsColumn";
import { PassengerColumn } from "@/components/table/PassengerColumn";
import orderColumn from "@/components/table/orderColumn";
import { HotelClinicArrivalTableProps } from "@/constants/hotelClinicArrivalSections";
import { PERMISSIONS } from "@/constants/permissions";
import { useClinicUnassignedDriverAssignProcessRowUpdate } from "@/hooks/useClinicUnassignedDriverAssignProcessRowUpdate";
import { useClinicUnassignedSaveAction } from "@/hooks/useClinicUnassignedSaveAction";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import { HotelClinicNames } from "@/models/hotel-clinic.model";
import { PlanNotesColumn } from "@/pages/Plannn/components/table/PlanNotesColumn";
import { PlanPickupColumn } from "@/pages/Plannn/components/table/PlanPickupColumn";
import useDriversContext from "@/providers/data/DriversContext";
import useAuth from "@/providers/hooks/useAuth";
import {
    ClinicArrivalDnDZoneId,
    ClinicArrivalDragPayload,
    ClinicArrivalDragPayloadTypes,
    ClinicArrivalOverTargetType,
    useClinicArrivalDndDrag,
    useClinicArrivalIsOverType,
} from "@/stores/use-clinic-arrival-dnd";
import {
    useClinicUnassignedIsFullScreen,
    useClinicUnassignedPassengersTableActions,
    useClinicUnassignedRowModesModel,
} from "@/stores/use-clinic-arrival-unassigned-passengers-table";
import { onGridCellKeyDownCallback } from "@/utils/grid";

import { HotelClinicListItem } from "../../types";
import { pickUpThenHotelComparator } from "../../utils/pickUpThenHotelComparator";
import { ClinicArrivalPassengerTypeColumn } from "./ClinicArrivalPassengerTypeColumn";
import ClinicArrivalRouteColumn from "./ClinicArrivalRouteColumn";
import { ClinicUnassignedActionsColumn } from "./ClinicUnassignedActionsColumn";
import { ClinicUnassignedAssignationColumn } from "./ClinicUnassignedAssignationColumn";
import HotelPickUpReasonColumn from "./HotelPickUpReasonColumn";
import UnassignedPassengersToolbar from "./UnassignedPassengersToolbar";

export const DraggablePassengerRow = React.forwardRef<HTMLDivElement, GridRowProps>(
    function DraggablePassengerRow(props, outerRef) {
        const isPlaceholder = Boolean(props.row?._isPlaceholder);

        const dragData: ClinicArrivalDragPayload = useMemo(
            () => ({
                id: props.row?.id ?? "",
                name: props.row?.name ?? "",
                uniqueIdentifier: props.row?.uniqueIdentifier ?? "",
                type: ClinicArrivalDragPayloadTypes.FROM_UNASSIGNED,
            }),
            [props.row?.id, props.row?.name, props.row?.uniqueIdentifier]
        );

        const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
            id: isPlaceholder ? `__unassigned_ph_drag__` : (props.row?.uniqueIdentifier ?? ""),
            disabled: isPlaceholder,
            data: dragData,
        });

        const mergedRef = (el: HTMLDivElement | null) => {
            if (!isPlaceholder) setNodeRef(el);
            if (typeof outerRef === "function") outerRef(el);
            else if (outerRef) (outerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        };

        if (isPlaceholder) {
            return (
                <GridRow
                    ref={(el: HTMLDivElement | null) => {
                        if (typeof outerRef === "function") outerRef(el);
                        else if (outerRef) (outerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                    }}
                    {...props}
                    style={{
                        ...props.style,
                        opacity: 0.5,
                        backgroundColor: "rgba(245,158,11,0.06)",
                        outline: "1.5px dashed rgba(245,158,11,0.5)",
                        outlineOffset: "-2px",
                        pointerEvents: "none",
                    }}
                />
            );
        }

        return (
            <GridRow
                ref={mergedRef}
                {...props}
                {...attributes}
                {...listeners}
                style={{
                    ...props.style,
                    opacity: isDragging ? 0 : 1,
                    cursor: "grab",
                    transition: isDragging ? undefined : "opacity 150ms ease",
                    userSelect: "none",
                }}
            />
        );
    }
);

const PLACEHOLDER_TEMPLATE: Omit<HotelClinicListItem, "name"> = {
    id: "__unassigned_ph__",
    uniqueIdentifier: "__unassigned_ph__",
    type: "CLINIC" as HotelClinicListItem["type"],
    matchKey: "",
    hotel: "",
    pickUp: "",
    name: "",
    userId: null,
    companions: [],
    reason: "OTHER" as HotelClinicListItem["reason"],
    notes: "",
    cancelled: false,
    _isPlaceholder: true,
    clinic: HotelClinicNames.ELIT_HAIR,
} as HotelClinicListItem;

const UNASSIGNED_ROWS_SCROLL_THRESHOLD = 6;
const UNASSIGNED_SECTION_MAX_HEIGHT = "min(45vh, calc(100dvh - 12rem))";

function UnAssignedPassengersTable({
    rows,
    loading,
    allRows,
    ...commonStatusProps
}: HotelClinicArrivalTableProps<HotelClinicListItem>) {
    const { setNodeRef: setDropRef } = useDroppable({
        id: ClinicArrivalDnDZoneId.Unassigned,
        data: {
            type: ClinicArrivalOverTargetType.Unassigned,
        },
    });
    const drag = useClinicArrivalDndDrag();
    const overUnassigned = useClinicArrivalIsOverType(ClinicArrivalOverTargetType.Unassigned);

    const displayRows = useMemo((): HotelClinicListItem[] => {
        if (!overUnassigned || !drag || rows.some((r) => r.uniqueIdentifier === drag.uniqueIdentifier)) return rows;
        return [
            ...rows,
            {
                ...PLACEHOLDER_TEMPLATE,
                name: drag.name,
                uniqueIdentifier: drag.uniqueIdentifier,
            } as HotelClinicListItem,
        ];
    }, [rows, overUnassigned, drag]);

    const { api } = useOutletContext<{ api: MutableRefObject<GridApiPremium> }>();
    const { handleSaveChanges, saveDisabled, isPending: isSavePending } = useClinicUnassignedSaveAction(api);
    const { t } = useTranslation();
    const { size } = useWindowDimensions();
    const { drivers } = useDriversContext();
    const rowModesModel = useClinicUnassignedRowModesModel();
    const { handleRowModesModelCancel, updateFullScreen } = useClinicUnassignedPassengersTableActions();
    const processRowUpdate = useClinicUnassignedDriverAssignProcessRowUpdate();
    const isFullScreen = useClinicUnassignedIsFullScreen();
    const useBoundedLayout = isFullScreen || displayRows.length >= UNASSIGNED_ROWS_SCROLL_THRESHOLD;

    // When the list empties (e.g. after all passengers are assigned), the toolbar — and with it the
    // fullscreen toggle — is replaced by the empty state, leaving no way to exit. Auto-close fullscreen.
    useEffect(() => {
        if (isFullScreen && !loading && rows.length === 0) {
            updateFullScreen(false);
        }
    }, [isFullScreen, loading, rows.length, updateFullScreen]);

    const selectionModel = useRef<GridCellSelectionModel | undefined>(undefined);
    const setCellSelectionModel = useCallback((selection: GridCellSelectionModel) => {
        selectionModel.current = selection;
    }, []);

    const userPermissions = useAuth()?.userData?.permissionCodes || [];
    const canUpdate = checkPermissions(userPermissions, [PERMISSIONS.TR_SVC_MANAGE_DRIVERS]);

    const onCellKeyDown = useCallback<GridEventListener<"cellKeyDown">>(
        ({ cellMode, hasFocus, field, isEditable, id }, event, ctx) => {
            onGridCellKeyDownCallback(
                { cellMode, hasFocus, field, isEditable },
                event,
                ctx,
                selectionModel.current || {},
                processRowUpdate,
                handleRowModesModelCancel,
                "userId"
            );
            if (event.key !== "Enter" || event.repeat || isSavePending) return;

            if (cellMode === "edit") {
                event.preventDefault();
                ctx.api.stopCellEditMode({ id, field });
                queueMicrotask(() => {
                    handleSaveChanges();
                });
                return;
            }

            if (saveDisabled) return;
            event.preventDefault();
            handleSaveChanges();
        },
        [isSavePending, handleSaveChanges, handleRowModesModelCancel, processRowUpdate, saveDisabled]
    );

    const columns: GridColDef[] = useMemo<GridColDef[]>(
        () => [
            orderColumn(),
            PassengerColumn({ size, t, raw: false }),
            CompanionsColumn({ t, editable: false }),
            ClinicArrivalRouteColumn({ t }),
            { ...PlanPickupColumn({ t, editable: false }), sortComparator: pickUpThenHotelComparator },
            HotelPickUpReasonColumn({ t }),
            ClinicArrivalPassengerTypeColumn({ t }),
            ClinicUnassignedAssignationColumn({ t, drivers }),
            PlanNotesColumn({ t, textClassName: "text-xs text-gray-600" }),
            ClinicUnassignedActionsColumn(),
        ],
        [size, t, drivers]
    );

    const unassignedGridSx = useMemo(
        () =>
            useBoundedLayout
                ? ({
                      flex: 1,
                      minHeight: 0,
                      height: "100%",
                      width: "100%" as const,
                      borderTop: "0px !important",
                      display: "flex",
                      flexDirection: "column",
                      "& .MuiDataGrid-main": {
                          flex: 1,
                          minHeight: 0,
                      },
                  } as const)
                : ({
                      width: "100%" as const,
                      borderTop: "0px !important",
                  } as const),
        [useBoundedLayout]
    );

    const sectionStyle =
        useBoundedLayout && !isFullScreen
            ? { height: UNASSIGNED_SECTION_MAX_HEIGHT, maxHeight: UNASSIGNED_SECTION_MAX_HEIGHT }
            : undefined;

    return (
        <>
            <div
                ref={setDropRef}
                style={{
                    borderColor: overUnassigned ? "rgba(245,158,11,0.6)" : "#f1f5f9",
                    backgroundColor: overUnassigned ? "rgba(254,243,199,0.4)" : "white",
                    boxShadow: overUnassigned ? "inset 0 0 0 2px rgba(245,158,11,0.4)" : "0 1px 3px 0 rgba(0,0,0,0.04)",
                    ...sectionStyle,
                }}
                className={`col-span-12 flex min-h-0 w-full min-w-0 flex-col shadow-sm border duration-150 transition-all ${
                    useBoundedLayout ? "overflow-hidden" : "h-auto overflow-visible"
                } ${isFullScreen ? "h-full" : ""}`}
            >
                <motion.div
                    className={`flex min-h-0 min-w-0 flex-col ${useBoundedLayout ? "h-full flex-1" : "w-full"}`}
                    initial={{ position: "static", width: "100%" }}
                    animate={{
                        height: isFullScreen ? "100vh" : useBoundedLayout ? "100%" : "auto",
                        width: isFullScreen ? "100vw" : "100%",
                        position: isFullScreen ? "fixed" : "static",
                        padding: isFullScreen ? (size.md ? 16 : 4) : 0,
                        top: 0,
                        left: 0,
                        background: isFullScreen ? "white" : "transparent",
                        zIndex: isFullScreen ? 1000 : "auto",
                    }}
                    transition={{ duration: 0.3 }}
                >
                    {displayRows.length === 0 && !loading ? (
                        <div className="flex min-h-[4.5rem] flex-col items-center justify-center gap-2 px-4 py-6">
                            <InboxOutlined className="h-10 w-10 shrink-0 text-slate-400" aria-hidden />
                            <p className="max-w-md text-center text-sm leading-snug text-slate-600">
                                {t("clinicArrival.noUnassignedPassengers")}
                            </p>
                        </div>
                    ) : (
                        <CommonGridTable<HotelClinicListItem>
                            api={api}
                            autoHeight={!useBoundedLayout}
                            gridSx={unassignedGridSx}
                            rowSelectionModel={[]}
                            loading={loading ?? false}
                            rows={displayRows}
                            columns={columns}
                            allRows={allRows}
                            rowModesModel={rowModesModel}
                            processRowUpdate={processRowUpdate}
                            CustomGridRow={DraggablePassengerRow}
                            hideFooter
                            getRowId={(row) => row.uniqueIdentifier}
                            preventCellEdit={!canUpdate}
                            onCellKeyDown={onCellKeyDown}
                            onCellSelectionModelChange={setCellSelectionModel}
                            isRowSelectable={(p) => !p.row._isPlaceholder}
                            toolbar={() => (
                                <UnassignedPassengersToolbar apiRef={api} rows={rows} {...commonStatusProps} />
                            )}
                        />
                    )}
                </motion.div>
            </div>
        </>
    );
}

export default UnAssignedPassengersTable;
